import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AppResource } from "@app/app.resource";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { BaseComponent } from "@shared/components/base/base.component";
import { DocumentsCardComponent } from "@shared/components/documents-card/documents-card.component";
import { AddressMrhComponent } from "@shared/components/offers/MRH/address-mrh/address-mrh.component";
import { DetailInfoCardMrhComponent } from "@shared/components/offers/MRH/detail-info-card-mrh/detail-info-card-mrh.component";
import { DetailTitleMrhComponent } from "@shared/components/offers/MRH/detail-title-mrh/detail-title-mrh.component";
import { OptionsListMrhComponent } from "@shared/components/offers/MRH/options-list-mrh/options-list-mrh.component";
import { ScholarInsuranceMrhComponent } from "@shared/components/offers/MRH/scholar-insurance-mrh/scholar-insurance-mrh.component";
import { DetailInfoCardPjComponent } from "@shared/components/offers/PJ/detail-info-card-pj/detail-info-card-pj.component";
import { DetailTitlePjComponent } from "@shared/components/offers/PJ/detail-title-pj/detail-title-pj.component";
import { OptionsListPJComponent } from "@shared/components/offers/PJ/options-list-pj/options-list-pj.component";
import { TitlePjComponent } from "@shared/components/offers/PJ/title-pj/title-pj.component";
import { DeclarationSaComponent } from "@shared/components/offers/SA/declaration-sa/declaration-sa.component";
import { DetailInfoCardSaComponent } from "@shared/components/offers/SA/detail-info-card-sa/detail-info-card-sa.component";
import { DetailTitleSaComponent } from "@shared/components/offers/SA/detail-title-sa/detail-title-sa.component";
import { MedicalHistorySaComponent } from "@shared/components/offers/SA/medical-history-sa/medical-history-sa.component";
import { OptionsListSAComponent } from "@shared/components/offers/SA/options-list-sa/options-list-sa.component";
import { TitleSaComponent } from "@shared/components/offers/SA/title-sa/title-sa.component";
import { StatusCardComponent } from "@shared/components/status-card/status-card.component";
import { NOM_ANI } from "@shared/constants/risk-item-fields.constants";
import { ID } from "@shared/constants/route-variables.constants";
import { ACCUEIL } from "@shared/constants/route.constants";
import { Branches } from "@shared/enums/branches";
import { DocumentType } from "@shared/enums/documents.type";
import { QuotationStates } from "@shared/enums/quotation-states";
import { Address } from "@shared/models/address";
import { SchoolInsuredChild } from "@shared/models/policy-details";
import { DocumentName, QuotationDetail } from "@shared/models/quotation";
import { StatusConfig } from "@shared/models/status-config.model";
import { OffersTypeService } from "@shared/services/offers-type.service";
import { QuotationRecoveryService } from "@shared/services/quotation-recovery.service";
import { QuotationStatusService } from "@shared/services/quotation-status.service";
import { QuotationService } from "@shared/services/quotation.service";
import { Guid } from "guid-typescript";
import { ButtonModule } from "primeng/button";
import { TabViewModule } from "primeng/tabview";

@Component({
  selector: "app-quotation-detail",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    StatusCardComponent,
    TabViewModule,
    DocumentsCardComponent,
    DetailTitleMrhComponent,
    AddressMrhComponent,
    DetailInfoCardMrhComponent,
    OptionsListMrhComponent,
    ScholarInsuranceMrhComponent,
    DetailTitlePjComponent,
    TitlePjComponent,
    TitleSaComponent,
    DetailTitleSaComponent,
    DetailInfoCardPjComponent,
    DetailInfoCardSaComponent,
    OptionsListPJComponent,
    OptionsListSAComponent,
    DeclarationSaComponent,
    MedicalHistorySaComponent,
  ],
  templateUrl: "./quotation-detail.component.html",
  styleUrl: "./quotation-detail.component.scss",
})
export class QuotationDetailsComponent extends BaseComponent implements OnInit {
  quotationId!: Guid;
  reference!: string;
  quotationStatusConfig!: Record<string, StatusConfig>;
  activeIndex = 0;

  branches = Branches;
  quotationDetails!: QuotationDetail;
  addressData!: Address;
  child!: SchoolInsuredChild[];
  quotationStates = QuotationStates;

  animalName!: string;

  documentTypes: DocumentName[] = [
    {
      type: DocumentType.DEV,
      name: "document.devis",
    },
    {
      type: DocumentType.FIC,
      name: "document.ficheInformationEtConseil",
    },
    {
      type: DocumentType.DIPA,
      name: "document.ficheDIPA",
    },
    {
      type: DocumentType.CG,
      name: "document.conditionsGenerales",
    },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private quotationStatusService: QuotationStatusService,
    private quotations: QuotationService,
    private readonly quotationRecoveryService: QuotationRecoveryService,
    private readonly offersTypeService: OffersTypeService,
    private readonly quotationService: QuotationService,
    resources: AppResource,
    private translate: TranslateService
  ) {
    super(resources);
  }

  resetView() {
    this.router.navigate(["/" + ACCUEIL]);
  }

  ngOnInit() {
    this.quotationId = this.route.snapshot.params[ID];
    this.quotationStatusConfig = this.quotationStatusService.getStatusConfig();
    this.loadQuotationDetails();
  }

  loadQuotationDetails() {
    this.quotations.getQuotationDetails(this.quotationId).subscribe({
      next: quotationDetails => {
        this.quotationDetails = quotationDetails;
        this.child = quotationDetails.schoolInsuredChildsVM.childrenList;
        this.reference = this.quotationDetails.reference;
        this.fillAddressData();
        this.isBranchMRH = this.offersTypeService.isQuotationDetailsBranch(this.quotationDetails, Branches.MRH);
        if (!this.isBranchMRH) {
          this.isBranchPJ = this.offersTypeService.isQuotationDetailsBranch(this.quotationDetails, Branches.PJ);
          if (!this.isBranchPJ) {
            this.fillSaData();
            this.isBranchSA = this.offersTypeService.isQuotationDetailsBranch(this.quotationDetails, Branches.SA);
          }
        }
      },
      error: err => {
        console.error("Failed to load quotation details", err);
      },
    });
  }

  fillAddressData() {
    if (this.quotationDetails) {
      this.addressData = {
        address: this.quotationDetails.address,
        postalCode: this.quotationDetails.postalCode,
        city: this.quotationDetails.city,
      };
    }
  }

  fillSaData() {
    if (this.quotationDetails) {
      this.animalName =
        this.quotationDetails.riskItemInformations[0]?.values.find(ri => ri.code === NOM_ANI)?.value || "";
    }
  }

  activeIndexChange(event: any) {
    this.activeIndex = event.index;
  }
  quotationSubscription() {
    this.quotationRecoveryService.quotationRecovery(
      this.quotationDetails.reference,
      this.quotationDetails.productCode,
      this.quotationDetails.branchCode,
      this.quotationDetails.distributorCode,
      this.quotationDetails.email
    );
  }

  getCustomStatus(quotation: QuotationDetail): string {
    return this.quotationService.getCustomStatus(quotation);
  }
}
